/**
 * 履歴一覧の共通部品 (マスタ変更履歴 SBI 7)。① 変更一覧と ② 対象データの履歴一覧で共有する。
 *
 * - 表示だけを担当する (データ取得・絞り込み・遷移先の決定は呼び出し側)。
 * - 読み込み中 / エラー / 「履歴はありません」/ 続きの読み込みを、①② で同じ文言にそろえる。
 * - `entity_key` は画面に出さない (対象は `display_name` で示す)。表示名が無い場合のみ
 *   「(名称なし)」とする。
 * - 行は Enter / Space でも開ける (キーボード操作)。操作の種別は色ではなく文言で示す。
 * - 変更前後の値は出さない (③ だけで表示する)。一覧に出すのは API が返す `summary` まで。
 * - 列幅は `table-layout: fixed` + <colgroup> で固定し、横スクロールを出さない (長い値は折り返す)。
 *   幅は styles.css の `.master-history-table` を参照。
 * - ① (`showTarget`) は対象ごとに 1 行 (呼び出し側が最新の 1 件に絞って渡す)。そのため見出しは
 *   「最終操作」「最終変更者」とし、変更概要の列は出さない (概要は ② で確認する)。
 * - `onRestore` を渡すと、各行の末尾に「データ復旧」ボタンの列を出す (① だけ)。最終操作が「削除」の
 *   行だけ押せる。端末は復旧の対象外なので、削除でも押せない。ボタンは行の遷移を起こさない。
 */
import type React from 'react';

import type { MasterHistoryAction, MasterHistoryListEntry, MasterHistoryType } from '../api/types';
import { formatDateTime } from '../utils/dateTime';
import {
  MASTER_HISTORY_ACTION_LABEL,
  MASTER_HISTORY_TYPE_LABEL,
} from '../utils/masterHistoryLabels';

import { Spinner } from './Spinner';
import { StatusBanner } from './StatusBanner';

const EMPTY_HISTORY_MESSAGE = '履歴はありません';
const NO_NAME_LABEL = '(名称なし)';
const NO_SUMMARY_LABEL = '—';
export const RESTORE_BUTTON_LABEL = 'データ復旧';
export const RESTORE_HINT_NOT_DELETED = '最終操作が「削除」のデータだけ復旧できます';
export const RESTORE_HINT_DEVICE = '端末は復旧できません';

export interface MasterHistoryTableProps {
  items: readonly MasterHistoryListEntry[];
  loading: boolean;
  loadingMore: boolean;
  /** 取得失敗時のメッセージ (表示用に変換済み)。null なら表示しない */
  errorMessage: string | null;
  hasMore: boolean;
  /** ① は true (データ種別・対象名の列を出す。変更概要の列は出さない)。② は false (対象が 1 つに決まっているため) */
  showTarget: boolean;
  onSelect: (entry: MasterHistoryListEntry) => void;
  /** 指定すると「データ復旧」の列を出す (① だけ)。押されたときに呼ぶ */
  onRestore?: (entry: MasterHistoryListEntry) => void;
  /** 復旧の実行中。true の間は全行のボタンを押せなくする (二重実行の防止) */
  restoreBusy?: boolean;
  onLoadMore: () => void;
  onReload: () => void;
  /** `data-testid` の接頭辞 */
  testIdPrefix: string;
}

function typeLabel(masterType: MasterHistoryType): string {
  return MASTER_HISTORY_TYPE_LABEL[masterType] ?? masterType;
}

function actionLabel(action: MasterHistoryAction): string {
  return MASTER_HISTORY_ACTION_LABEL[action] ?? action;
}

/** 復旧できる行か。最終操作が削除で、端末ではないとき。 */
export function isRestorable(entry: MasterHistoryListEntry): boolean {
  return entry.action === 'DELETE' && entry.master_type !== 'device';
}

function restoreHint(entry: MasterHistoryListEntry): string | undefined {
  if (isRestorable(entry)) return undefined;
  return entry.master_type === 'device' ? RESTORE_HINT_DEVICE : RESTORE_HINT_NOT_DELETED;
}

export function MasterHistoryTable(props: MasterHistoryTableProps): React.JSX.Element {
  const {
    items,
    loading,
    loadingMore,
    errorMessage,
    hasMore,
    showTarget,
    onSelect,
    onRestore,
    restoreBusy = false,
    onLoadMore,
    onReload,
    testIdPrefix,
  } = props;
  const showRestore = showTarget && onRestore !== undefined;
  const columnCount = (showTarget ? 5 : 4) + (showRestore ? 1 : 0);
  const showEmpty = !loading && errorMessage === null && items.length === 0;

  return (
    <div className="list-page__body" data-testid={`${testIdPrefix}-table-area`}>
      {errorMessage !== null && (
        <StatusBanner variant="error" data-testid={`${testIdPrefix}-error`}>
          <span>{errorMessage}</span>{' '}
          <button type="button" onClick={onReload} data-testid={`${testIdPrefix}-retry`}>
            再読み込み
          </button>
        </StatusBanner>
      )}
      <div className="table-wrapper">
        <table
          className={`data-table master-history-table ${
            showTarget ? 'master-history-table--list' : 'master-history-table--entity'
          }`}
          data-testid={`${testIdPrefix}-table`}
        >
          <colgroup>
            <col className="master-history-table__col--changed-at" />
            {showTarget && <col className="master-history-table__col--type" />}
            {showTarget && <col className="master-history-table__col--target" />}
            <col className="master-history-table__col--action" />
            {!showTarget && <col />}
            <col className="master-history-table__col--actor" />
            {showRestore && <col className="master-history-table__col--restore" />}
          </colgroup>
          <thead>
            <tr>
              <th scope="col">変更日時</th>
              {showTarget && <th scope="col">データ種別</th>}
              {showTarget && <th scope="col">対象名</th>}
              <th scope="col">{showTarget ? '最終操作' : '操作'}</th>
              {!showTarget && <th scope="col">変更概要</th>}
              <th scope="col">{showTarget ? '最終変更者' : '変更者'}</th>
              {showRestore && <th scope="col">復旧</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columnCount} className="data-table__empty">
                  <Spinner /> 読み込み中...
                </td>
              </tr>
            )}
            {showEmpty && (
              <tr>
                <td
                  colSpan={columnCount}
                  className="data-table__empty"
                  data-testid={`${testIdPrefix}-empty`}
                >
                  {EMPTY_HISTORY_MESSAGE}
                </td>
              </tr>
            )}
            {!loading &&
              items.map((entry) => (
                <tr
                  key={`${entry.entity_key}/${entry.version}`}
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  data-testid={`${testIdPrefix}-row-${entry.version}`}
                  onClick={() => onSelect(entry)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(entry);
                    }
                  }}
                >
                  <td>{formatDateTime(entry.changed_at)}</td>
                  {showTarget && <td>{typeLabel(entry.master_type)}</td>}
                  {showTarget && <td>{entry.display_name || NO_NAME_LABEL}</td>}
                  <td>{actionLabel(entry.action)}</td>
                  {!showTarget && <td>{entry.summary || NO_SUMMARY_LABEL}</td>}
                  <td>{entry.actor || NO_SUMMARY_LABEL}</td>
                  {showRestore && (
                    <td>
                      <button
                        type="button"
                        disabled={!isRestorable(entry) || restoreBusy}
                        title={restoreHint(entry)}
                        // 行の遷移 (クリック / Enter / Space) を起こさない。
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestore(entry);
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        data-testid={`${testIdPrefix}-restore-${entry.version}`}
                      >
                        {RESTORE_BUTTON_LABEL}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {!loading && hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loadingMore}
          data-testid={`${testIdPrefix}-load-more`}
        >
          {loadingMore ? '読み込み中...' : 'さらに読み込む'}
        </button>
      )}
    </div>
  );
}
